/**
 * **连线：接上 / 新建并接上** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 两件事：
 *
 *   1. `connectByOrigin` —— origin 可能是输入（反向找输出），统一成 source → target
 *   2. `linkCandidateSections` —— 拖线松手落空时的候选（Blender 的 link drag search）
 *
 * 第 2 件分两段，跟 Blender 一致：
 *   ① **已有的**端口 —— 直接接上
 *   ② **可新建的**节点类型 —— 建出来并接上
 * 第二段是这个交互的精髓：不用先建节点再接，而是「我想连到什么」，
 * 节点顺手就有了。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `applyConnection` / `addNodeAt` / `findSocket` / `currentModels` /
 * `isStructurallyValid` 由 `ctx` 注入 —— 它们分属别的段落，一个都不搬。
 */
import { nextTick } from 'vue'
import { canConnect } from '@/types/socket'
import type { GraphNodeModel, NodeRow, SocketDef } from '@/types/node'
import {
  nextRepeaterIndex,
  repeaterCount,
  repeaterHandle,
  repeaterInstanceSocket,
  repeaterSide,
} from '@/node/rows'
import { NODE_TYPES } from '@/playground/nodeTypes'
import type { NodeTypeDef } from '@/playground/nodeTypes'
import type { MenuItem, MenuSection } from '@/types/menu'
import type { EdgeRef, HandleRef } from '@/graph/canvasGesture'

/**
 * 拖线松手落空时那次菜单的上下文。
 *
 * ⚠️ 本地定义，不从 Playground 导 —— composable 不该反向依赖宿主文件。
 * 结构上与宿主的 `LinkMenuContext` 相容（那边多一个可选 `undoEdge`，
 * 本文件用不到）。
 */
export interface LinkMenuContext {
  origin: HandleRef
  point: { x: number; y: number }
  /** 从已连线的输入拖出来时带的原边：菜单被关掉（没选东西）就是「断开」 */
  undoEdge?: EdgeRef
}

export interface ConnectCtx {
  /** 真正把一条连线落到图上（在「状态机接线」段里，不搬） */
  applyConnection: (conn: {
    source: string
    sourceHandle: string | null
    target: string
    targetHandle: string | null
  }) => void
  /** 新建节点（来自 useAddNode） */
  addNodeAt: (type: string, screenPoint?: { x: number; y: number }) => string | null
  /** 端口查询（来自 useSubgraphLevels 的注入） */
  findSocket: (nodeId: string, handleId: string | null | undefined) => SocketDef | undefined
  /** 当前这一层的节点模型 */
  currentModels: () => GraphNodeModel[]
  /** 结构校验（类型 + 自连 + 环） */
  isStructurallyValid: (
    sourceNodeId: string,
    sourceHandleId: string | null | undefined,
    targetNodeId: string,
    targetHandleId: string | null | undefined,
  ) => boolean
  commit: (label: string, coalesceMs?: number) => void
}

export function useConnect(ctx: ConnectCtx) {
  const { applyConnection, addNodeAt, findSocket, currentModels, isStructurallyValid, commit } = ctx

/** origin 可能是输入（反向找输出），统一成 source → target */
function connectByOrigin(origin: HandleRef, other: HandleRef) {
  const source = origin.type === 'source' ? origin : other
  const target = origin.type === 'source' ? other : origin

  commit('连线')
  applyConnection({
    source: source.nodeId,
    sourceHandle: source.handleId,
    target: target.nodeId,
    targetHandle: target.handleId,
  })
}


/** 新建一个节点，并把它接到 origin 上（落在松手的位置） */
function createAndConnect(
  def: NodeTypeDef,
  handleId: string,
  origin: HandleRef,
  point: { x: number; y: number },
) {
  const newId = addNodeAt(def.type, point)
  if (!newId) return
  nextTick(() =>
    connectByOrigin(origin, {
      nodeId: newId,
      handleId,
      type: origin.type === 'source' ? 'target' : 'source',
    }),
  )
}

/**
 * 扫一个节点类型上所有能接上 origin 的端口。
 *
 * 变参行只报**下一个空位** —— 接上去就是新增一个实例，
 * 这跟用户在界面上看到的那一行是对应的。
 */
function slotsOf(
  rows: readonly NodeRow[],
  originSocket: SocketDef,
  wantInputs: boolean,
): { handleId: string; label: string; socket: SocketDef }[] {
  const slots: { handleId: string; label: string; socket: SocketDef }[] = []

  for (const row of rows) {
    if (wantInputs && row.kind === 'input') {
      if (canConnect(originSocket, row.socket)) {
        slots.push({
          handleId: row.socket.id,
          label: row.label ?? row.socket.id,
          socket: row.socket,
        })
      }
      continue
    }
    if (!wantInputs && row.kind === 'output') {
      if (canConnect(row.socket, originSocket)) {
        slots.push({ handleId: row.socket.id, label: row.label ?? row.socket.id, socket: row.socket })
      }
      continue
    }
    if (row.kind === 'repeater') {
      const side = repeaterSide(row)
      if (wantInputs && side === 'input') {
        /*
         * 输入方向：多给一个**还不存在的**实例（「新建一项并接上」）。
         *
         * ⚠️⚠️ 用 `nextRepeaterIndex`（= 最后一个已连实例的下一个），
         * **不是** `repeaterCount` —— 后者是**渲染件数**，两者差一：
         *
         * ```text
         *   一个都没连时：repeaterCount = 1
         *                 （那 1 行就是留给你的空槽，下标是 0）
         *                 但"下一个空位"是 0，不是 1
         *   → 菜单写着「监听 第 2 项」，建出来的节点第 1 项空着
         *     （真机反馈：「不应该连到监听1吗，而且事实确实默认是监听2」）
         * ```
         *
         * 这里原来就是 `repeaterCount(row)`，注释还替它辩护了一句
         * （"已经含末尾那个空槽，所以下标正好是下一个"）—— 那句话是错的。
         */
        const index = nextRepeaterIndex(row)
        const socket = repeaterInstanceSocket(row, index)
        if (canConnect(originSocket, socket)) {
          slots.push({
            handleId: repeaterHandle(row, index),
            label: `${row.label ?? row.id} 第 ${index + 1} 项`,
            socket,
          })
        }
      } else if (!wantInputs && side === 'output') {
        /*
         * ★ 输出方向**没有「多一个」这一说** —— 数量是节点声明的，
         * 空槽没有意义（见 `RepeaterRow.side` 的注释）。
         * 所以只列**已存在**的实例。
         */
        const total = repeaterCount(row)
        for (let index = 0; index < total; index += 1) {
          const socket = repeaterInstanceSocket(row, index)
          if (!canConnect(socket, originSocket)) continue
          slots.push({
            handleId: repeaterHandle(row, index),
            label: `${row.label ?? row.id} 第 ${index + 1} 项`,
            socket,
          })
        }
      }
    }
  }
  return slots
}

/**
 * 拖线松手落空时的候选（Blender 的 link drag search）。
 *
 * 分两段，跟 Blender 一致：
 *   ① **已有的**端口 —— 直接接上
 *   ② **可新建的**节点类型 —— 建出来并接上
 * 第二段是 Blender 这个交互的精髓：不用先建节点再接，
 * 而是「我想连到什么」，节点顺手就有了。
 */
function linkCandidateSections(ctx: LinkMenuContext): MenuSection[] {
  const origin = ctx.origin
  const originSocket = findSocket(origin.nodeId, origin.handleId)
  if (!originSocket) return []

  // 从输出起线 → 找输入；从输入起线（反向）→ 找输出
  const wantInputs = origin.type === 'source'
  const existing: MenuItem[] = []

  for (const model of currentModels()) {
    if (model.id === origin.nodeId) continue
    for (const slot of slotsOf(model.rows, originSocket, wantInputs)) {
      const target: HandleRef = {
        nodeId: model.id,
        handleId: slot.handleId,
        type: wantInputs ? 'target' : 'source',
      }
      // 已有的端口要过完整校验（类型 + 自连 + 环）
      const ok = wantInputs
        ? isStructurallyValid(origin.nodeId, origin.handleId, model.id, slot.handleId)
        : isStructurallyValid(model.id, slot.handleId, origin.nodeId, origin.handleId)
      if (!ok) continue

      existing.push({
        id: `link:${model.id}:${slot.handleId}`,
        label: `${model.label ?? model.title} · ${slot.label}`,
        keywords: [model.title, model.type, slot.label, slot.handleId],
        run: () => connectByOrigin(origin, target),
      })
    }
  }

  const creatable: MenuItem[] = []
  for (const def of NODE_TYPES) {
    // 新建的节点不可能成环，所以只需要类型兼容
    const slots = slotsOf(def.rows, originSocket, wantInputs)
    const first = slots[0]
    if (!first) continue
    creatable.push({
      id: `new:${def.type}`,
      label: `新建 ${def.title}`,
      hint: first.label,
      keywords: def.keywords,
      run: () => createAndConnect(def, first.handleId, origin, ctx.point),
    })
  }

  const sections: MenuSection[] = []

  if (existing.length > 0) {
    sections.push({ id: 'link-existing', title: '接上已有的', items: existing })
  }
  if (creatable.length > 0) {
    sections.push({ id: 'link-new', title: '新建并接上', items: creatable })
  }
  return sections
}

  return { connectByOrigin, createAndConnect, slotsOf, linkCandidateSections }
}
